import React from 'react';

import { LoadingButton } from '@atlaskit/button';
import SectionMessage from '@atlaskit/section-message';
import TextField from '@atlaskit/textfield';

import jira from 'src/lib/api/jira';
import pd from 'src/lib/api/pagerduty';
import { textifyError } from 'src/lib/error';
import { locationReload } from 'src/lib/history';

import CenteredSpinner from 'src/views/components/CenteredSpinner';
import ErrorMessage from 'src/views/components/ErrorMessage';
import Field from 'src/views/components/Form/Field';

class SettingsField extends React.Component {
  state = {
    value: '',
    loading: false,
    validation: null,
    error: null,
  };

  componentDidMount() {
    const { initialValue, validateOnLoad } = this.props;
    this.setState({ value: initialValue || '' });
    if (initialValue && validateOnLoad) {
      this.setState({ validation: 'validating...' });
      validateOnLoad(initialValue)
        .then(() => this.setState({ validation: 'successfully validated' }))
        .catch((e) => this.setState({ validation: null, error: textifyError(e) }));
    }
  }

  onRemoveClick = async () => {
    if (await jira.confirm(`Are you sure you want to remove ${this.props.name}`)) {
      this.setState({ loading: true });
      this.props.onRemove()
        .then(() => locationReload())
        .catch(error => this.setState({ error, loading: false }));
    }
  };

  onSaveClick = () => {
    if (!this.state.value) {
      this.setState({ error: 'Must not be empty' });
      return;
    }

    this.setState({ loading: true, error: null });
    jira
      .setSettings({
        [this.props.name]: {
          value: this.state.value,
          secure: true,
          exposeLastChars: 3
        }
      })
      .then(() => locationReload())
      .catch(error => this.setState({ error, loading: false }));
  };

  onValueChange = (e) => {
    this.setState({ value: e.target.value });
  };

  render() {
    const { label, helperMessage, name, initialValue } = this.props;
    const { value, loading, error, validation } = this.state;

    const RemoveButton = () => <LoadingButton appearance="danger" isLoading={loading} onClick={this.onRemoveClick}>Remove</LoadingButton>;
    const SaveButton = () => <LoadingButton appearance="primary" isLoading={loading} onClick={this.onSaveClick}>Save</LoadingButton>;

    return (
      <Field
        label={label}
        helperMessage={helperMessage}
        validMessage={validation}
        errorMessage={error}
      >
        <div style={{ display: 'flex' }}>
          <TextField
            value={value}
            onChange={this.onValueChange}
            isDisabled={Boolean(initialValue)}
            name={name}
          />
          <div style={{ marginLeft: 10, alignSelf: 'center' }}>
            { initialValue ? <RemoveButton/> : <SaveButton/> }
          </div>
        </div>
      </Field>
    );
  }
}

const InfoMessage = ({ v3IssuedAt, v3Domain }) => Boolean(v3IssuedAt && v3Domain) && (
  <div style={{ marginBottom: 20, marginTop: 20 }}>
    <div>
      <span style={{ width: '18rem', fontWeight: 'bold' }}>Connected to: </span>{v3Domain}
    </div>
    <div>
      <span style={{ width: '18rem', fontWeight: 'bold' }}>Connection time: </span>{new Date(Number(v3IssuedAt) * 1000).toLocaleString()}
    </div>
  </div>
);

const DontUseItMessage = () => {
  return (
    <SectionMessage title="App version 3 settings">
      These settings only take effect on rules that are defined on the PagerDuty side using Jira Server extension UI.<br/>
      Settings on this tab are app-wide and don&#39;t depend on the PagerDuty account you are logged in to.
    </SectionMessage>
  );
};

class Loader extends React.Component {
  state = {
    settings: null,
    error: null,
    loading: true,
  };

  componentDidMount() {
    jira.getSettings(['v3AuthToken', 'pdApiKey', 'v3IssuedAt', 'v3Domain'])
      .then(({ settings }) => this.setState({ settings, loading: false }))
      .catch(error => this.setState({ error, loading: false }));
  }

  render() {
    const { error, settings, loading } = this.state;
    if (error) return <ErrorMessage error={error}/>;
    if (loading) return <CenteredSpinner/>;
    let {
      v3AuthToken,
      pdApiKey,
      v3IssuedAt,
      v3Domain,
    } = settings;

    v3AuthToken = (v3AuthToken || {}).value;
    pdApiKey = (pdApiKey || {}).value;
    v3IssuedAt = (v3IssuedAt || {}).value;
    v3Domain = (v3Domain || {}).value;

    return (
      <div data-cy="legacy-jira-server-config">
        <form>
          <DontUseItMessage/>
          <InfoMessage v3Domain={v3Domain} v3IssuedAt={v3IssuedAt}/>

          <div data-cy="auth-token-setting-field">
            <SettingsField
              label="AuthToken from PagerDuty Extension page"
              helperMessage="The AuthToken is required to enable all automatic features and customizations. You can find AuthToken on Jira Extensions page in your PagerDuty account."
              initialValue={v3AuthToken}
              name="v3AuthToken"
              onRemove={() => jira.removeSettings(['v3AuthToken'])}
              validateOnLoad={() => pd
                .getLegacySidebarInfo('FOO', 'BAR')
                .catch(e => { throw new Error(`PagerDuty rejected the request, Token is not valid anymore`); })
              }
            />
          </div>
          <br/>
          <br/>
          <div data-cy="pdapikey-setting-field">
            <SettingsField
              label="PagerDuty REST API Key"
              helperMessage="The API Key is used to fetch advanced incident details (like Notes and Timeline) from PagerDuty. It's generated automatically when you save AuthToken"
              initialValue={pdApiKey}
              name="pdApiKey"
              onRemove={() => jira.setSettings({ pdApiKey: { value: null } })}
              validateOnLoad={() => pd.getAbilities({ Authorization: 'Token token={{settings.pdApiKey}}' }) }
            />
          </div>
        </form>
      </div>
    );
  }
}

export default Loader;
